import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Chip,
  Paper,
  CircularProgress,
} from '@mui/material';
import axios from 'axios';

interface Book {
  id: number;
  title: string;
  description: string;
  publication_year: string;
  page_count: string;
  cover_image_url: string;
  file_url: string;
  authors: number[];
  genres: number[];
}

const EditBook = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [book, setBook] = useState<Book | null>(null);
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const bookRes = await axios.get(`/api/books?id=${id}`);

      setBook(bookRes.data.book);
    } catch (err) {
      console.error('Error fetching data:', err);
      alert('Error loading book data');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [id, fetchData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    if (!book) return;
    
    setBook(prev => ({
      ...prev!,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'cover' | 'pdf') => {
    const file = e.target.files?.[0];
    if (file) {
      if (type === 'cover') setCoverImage(file);
      else setPdfFile(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!book) return;
    
    setSaving(true);
    try {
      const formData = new FormData();
      
      // Validate required fields
      if (!book.title) throw new Error('Title is required');
      
      // Append basic fields
      Object.entries(book).forEach(([key, value]) => {
        if (value && key !== 'cover_image_url' && key !== 'file_url') {
          formData.append(key, value.toString().trim());
        }
      });
      
      // Append files only if new ones were selected
      if (coverImage) formData.append('cover_image', coverImage);
      if (pdfFile) formData.append('pdf_file', pdfFile);

      // Log formData contents for debugging
      Array.from(formData.entries()).forEach(([key, value]) => {
        console.log(key, value);
      });

      await axios.put(`/api/books?id=${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      alert('Book updated successfully!');
      navigate('/admin');
    } catch (err) {
      console.error('Error updating book:', err);
      alert(err instanceof Error ? err.message : 'Error updating book');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!book) {
    return (
      <Container>
        <Typography>Book not found</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>Edit Book</Typography>
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Title"
                name="title"
                value={book.title}
                onChange={handleInputChange}
                margin="normal"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                name="description"
                value={book.description || ''}
                onChange={handleInputChange}
                margin="normal"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Publication Year"
                name="publication_year"
                value={book.publication_year || ''}
                onChange={handleInputChange}
                margin="normal"
                type="number"
                inputProps={{ min: 1800, max: new Date().getFullYear() }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Page Count"
                name="page_count"
                type="number"
                value={book.page_count || ''}
                onChange={handleInputChange}
                margin="normal"
              />
            </Grid>

            {/* Current Files Display */}
            {book.cover_image_url && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Current Cover Image:</Typography>
                <img 
                  src={book.cover_image_url.startsWith('http') ? book.cover_image_url : `/images/${book.cover_image_url}`} 
                  alt="Current cover" 
                  style={{ maxWidth: '200px', marginTop: '8px' }}
                />
              </Grid>
            )}

            {book.file_url && (
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Current PDF:</Typography>
                <Link to={book.file_url.startsWith('http') ? book.file_url : `https://z4l1g0tev5tfrvnx.public.blob.vercel-storage.com/${book.file_url}`} target="_blank">View Current PDF</Link>
              </Grid>
            )}

            {/* File Uploads */}
            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                component="label"
                fullWidth
                sx={{ mt: 2 }}
              >
                Update Cover Image
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'cover')}
                />
              </Button>
              {coverImage && (
                <Chip
                  label={coverImage.name}
                  onDelete={() => setCoverImage(null)}
                  sx={{ mt: 1 }}
                />
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                component="label"
                fullWidth
                sx={{ mt: 2 }}
              >
                Update PDF File
                <input
                  type="file"
                  hidden
                  accept=".pdf"
                  onChange={(e) => handleFileChange(e, 'pdf')}
                />
              </Button>
              {pdfFile && (
                <Chip
                  label={pdfFile.name}
                  onDelete={() => setPdfFile(null)}
                  sx={{ mt: 1 }}
                />
              )}
            </Grid>

            {/* Submit Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/admin')}
                >
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default EditBook; 